import { Component, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DoHttpService } from 'src/app/core/services/do-http.service';
import { LayoutService } from 'src/app/core/services/layout.service';
import { UserDetailService } from 'src/app/core/services/user-detail.service';
import { DrawoutAppProService } from 'src/app/modules/drawout-app-pro/services/drawout-app-pro.service';
import { DrawoutLiteService } from 'src/app/modules/drawout-lite/services/drawout-lite.service';
import { DrawoutTestSpaceService } from 'src/app/modules/drawout-test-space/services/drawout-test-space.service';
import UIAvatarSvg from 'src/app/shared/classes/avatar-maker';
import { ApiUrls } from 'src/app/shared/constants/ApiUrls';
import { DrawoutApps } from 'src/app/shared/enum/common/DrawoutApps';
import { Testspace } from 'src/app/shared/interfaces/DrawoutTestSpace/TestSpace';
import { OrganisationBasic } from 'src/app/shared/interfaces/IGeneralDetails';
import { LastSessionDto } from 'src/app/shared/interfaces/OtherInterfaces';
import { ProductTypeEnum } from 'src/app/shared/enum/ProductTypeEnum';
import { tsAdminAccess } from 'src/app/shared/constants/RoleConstants';

@Component({
  selector: 'app-home-page',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss']
})
export class HomePageComponent implements OnInit {

  selectedOrganisationId = this.userDetailService.getUserData().organisationId;
  testSpaces: Testspace[];
  drawoutApps = DrawoutApps;
  subscriptions: Subscription[] = [];
  isLoading = false;
  isAppsLoading = false;
  availableOrganisations = [];
  productType = ProductTypeEnum;

  constructor(public layoutService: LayoutService,
    public dproService: DrawoutAppProService,
    public dliteService: DrawoutLiteService,
    private request: DoHttpService,
    public sanitizer: DomSanitizer,
    private tsService: DrawoutTestSpaceService,
    private userDetailService: UserDetailService,
    private router: Router
  ) { }

  get isOrganisationAdmin() {
    return this.userDetailService.getUserData().isOrganisationAdmin;
  }

  /** Landing segment for a single-testspace shortcut — respects testspace role. */
  get testspaceLandingSegment(): string {
    const roles = this.userDetailService.getUserData().roles;
    return tsAdminAccess.some(r => roles.includes(r)) ? 'projects' : 'test-list';
  }

  get isDOUser() {
    return this.userDetailService.getUserData().isDORole;
  }

  get isSketchAvailable() {
    return this.userDetailService.getUserData().isSketchAvailable;
    return this.userDetailService.getUserData().organisationId === 'fabcd4c0-16a8-4e94-8b9f-653328fd146f';
  }

  get organisationHasSketch() {
    const sketchProdcuts = [
      ProductTypeEnum.EplanSketch,
      ProductTypeEnum.SEESketch,
      ProductTypeEnum.PCSSketch,
      ProductTypeEnum.EplanCTSSketch,
      ProductTypeEnum.SEECTSSketch,
      ProductTypeEnum.PCSCTSSketch
    ]
    return this.userDetailService.getUserData().organisationProducts.some(item => sketchProdcuts.includes(item));
  }

  get isZenConfigAvailable() {
    return this.userDetailService.getUserData().isConfigurationAvailable;
  }

  get organisationHasZenConfig() {
    return this.userDetailService.getUserData().organisationProducts.includes(ProductTypeEnum.LegacyZen);
  }

  get isTestspaceAvailable() {
    return this.userDetailService.getUserData().isTestspaceAvailable;
  }

  get organisationHasTestspace() {
    return this.userDetailService.getUserData().organisationProducts.includes(ProductTypeEnum.Testspace);
  }

  get isWorkspaceAvaialble() {
    return this.userDetailService.getUserData().isWorkspaceAvailable;
  }

  get organisationHasWorkspace() {
    return this.userDetailService.getUserData().organisationProducts.includes(ProductTypeEnum.DrawoutApp);
  }

  get isNoAppAvailable() {
    return !this.isSketchAvailable
      && !this.isZenConfigAvailable
      && !this.isTestspaceAvailable
      && (!this.isWorkspaceAvaialble || this.dproService.workspaces.length === 0)
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.layoutService.breadcrumbItems = [];
    this.layoutService.removeSideNav();
    this.layoutService.sideBarNavigationItems = [];
    this.navigatedTo(0)
    this.getMyOrganisations();
    this.loadInitialData();
  }

  loadInitialData() {
    this.selectedOrganisationId = this.userDetailService.getUserData().organisationId;
    this.loadTestspaces()
    if (this.isWorkspaceAvaialble) {
      this.dproService.loadWorkspaces();
    }
    if (this.isSketchAvailable) {
      this.dliteService.loadAvailableSketchApps();
    }
    this.isAppsLoading = false;
  }

  addNewOrganisation() {
    let user = this.userDetailService.getUserData();
    let userData = {
      name: user.fullName,
      email: user.userName
    };
    this.router.navigate(['add-organisation'], { queryParams: { name: user.fullName, email: user.userName } })
  }

  getMyOrganisations() {
    this.request.get(ApiUrls.getMyOrganisations).subscribe(res => {
      this.availableOrganisations = res;
      this.isLoading = false;
      if (this.selectedOrganisationId && this.availableOrganisations.length) {
        const requiredOrganisation = this.availableOrganisations.find(organisation => organisation.organisationId === this.selectedOrganisationId);
        this.switchOrganisation(requiredOrganisation, true);
      }
    })
  }

  loadTestspaces() {
    if (this.isTestspaceAvailable) {
      this.request.get(ApiUrls.getTestspacesApi(this.userDetailService.getUserData().organisationId)).subscribe(res => {
        this.testSpaces = res.data;
      })
    }
  }

  switchOrganisation(organisation, isFirstLoad: boolean = false) {
    // this.isLoading = true;
    if (isFirstLoad || organisation.organisationId !== this.selectedOrganisationId) {
      this.isAppsLoading = true;
      this.dproService.workspaces = [];
      this.testSpaces = [];
      this.request.get(`${this.request.clientBaseUrl}/switch-organisation?organisationId=${organisation.organisationId}`).subscribe(result => {
        sessionStorage.xCompuse = result.xCompuse;
        sessionStorage.fullName = result.fullName ? result.fullName : '';
        sessionStorage.organisationCode = result.organisationCode;
        this.userDetailService.setUser(result, false);
        this.loadInitialData();
      });
    }
  }

  getWorkspaceSVG(workspaceName, sanitizer: DomSanitizer) {
    const validLettersRegex = /^[a-zA-Z0-9]$/; // Validate the user input to avoid possible XSS attack.
    const singleLetter = workspaceName[0];
    const svg = new UIAvatarSvg();
    svg.borderRadius(8);
    svg.textColor("#fff");
    svg.bgColor("#0fa2d9");
    svg.size(56);
    svg.fontWeight(700);
    svg.fontSize(0.5);

    if (validLettersRegex.test(singleLetter)) {
      svg.text(singleLetter);
    } else {
      svg.text("W");
    }

    return sanitizer.bypassSecurityTrustHtml(svg.getInnerHTML())
  }

  navigatedTo(area, id = null) {
    const data: LastSessionDto = {
      appLastSession: area,
      lastSessionId: id
    }
    this.request.put(ApiUrls.lastUsedAppregister, data, true).subscribe();
  }
}
